import React, { useEffect, useMemo, useState } from 'react';
import { FiX, FiShield, FiInfo } from 'react-icons/fi';
import { useAuthStore } from '../../stores';
import { useApi } from '../../hooks/useApi';
import { showToast } from '../../utils/toast';
import { User } from '../../types';
import { LoadingButton } from '../Common/Loader';
type Props = {
  isOpen: boolean;
  onClose: () => void;
  user: User;
  onSaved?: () => void;
};

function formatUsd(n: number): string {
  if (!Number.isFinite(n)) return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);
}

export default function WebullRiskManagementModal({ isOpen, onClose, user, onSaved }: Props) {
  const setUser = useAuthStore((s) => s.setUser);
  const [pctInput, setPctInput] = useState(String(user.optionRm ?? 10));
  const [agreedToDisclaimer, setAgreedToDisclaimer] = useState(false);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setPctInput(String(user.optionRm ?? 10));
      setAgreedToDisclaimer(false);
      setShowModal(false);
    }
  }, [isOpen, user.optionRm]);

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    if (checked) {
      setShowModal(true);
    } else {
      setAgreedToDisclaimer(false);
    }
  };

  const dayBal = Number(user.webullDayBalance ?? 0);
  const recommended = dayBal > 6000 ? 10 : 20;

  const pctNum = parseFloat(pctInput.replace(',', '.'));
  const validPct = Number.isFinite(pctNum) && pctNum >= 1 && pctNum <= 100;

  const calculation = useMemo(() => {
    if (!validPct) return null;
    const p = pctNum;
    if (dayBal > 0) {
      const lossCap = (dayBal * p) / 100;
      const equityFloor = dayBal - lossCap;
      return {
        title: `Your ${p}% limit means:`,
        lines: [
          `Day-start baseline (Webull): ${formatUsd(dayBal)}.`,
          `Copy trading pauses when today’s drawdown reaches ${p}% — about ${formatUsd(lossCap)} below that baseline.`,
          `That is roughly when equity is at or below ${formatUsd(equityFloor)} (same day, US Eastern time).`,
        ],
      };
    }
    return {
      title: `Your ${p}% limit means:`,
      lines: [
        'Your Webull day-start balance is set and refreshed by the system (often near the session).',
        `Once today’s loss reaches ${p}% of that baseline, Webull copy trading pauses until the end of the trading day (ET).`,
      ],
    };
  }, [dayBal, pctNum, validPct]);

  const updateMutation = useApi.useUpdateOptionRm({
    onSuccess: (data) => {
      if (data.user) setUser(data.user);
      showToast('success', 'Webull risk limit saved.');
      onSaved?.();
      onClose();
    },
    onError: (err: any) => {
      showToast('error', err.response?.data?.error || 'Could not save risk limit.');
    },
  });

  const handleSave = () => {
    if (!agreedToDisclaimer) {
      showToast('error', 'Please confirm that you have read the disclosure and agree to proceed.');
      return;
    }
    if (!validPct) {
      showToast('error', 'Enter a percent between 1 and 100.');
      return;
    }
    updateMutation.mutate(Math.round(pctNum * 100) / 100);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[60] p-3 sm:p-4">
      <div className="bg-surface-elevated rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto border border-border">
        <div className="sticky top-0 bg-surface-elevated border-b border-border px-4 sm:px-5 py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="p-2.5 rounded-lg bg-sky-500/15 text-sky-600 dark:text-sky-400 shrink-0">
              <FiShield className="h-6 w-6" />
            </div>
            <div className="min-w-0">
              <h2 className="text-lg font-bold text-foreground truncate">Webull Risk Management</h2>
              <p className="text-xs text-muted">Daily loss limit for Webull copy trades</p>
            </div>
          </div>
          <button type="button" onClick={onClose} className="text-muted hover:text-foreground p-1 shrink-0" aria-label="Close">
            <FiX className="h-6 w-6" />
          </button>
        </div>

        <div className="p-4 sm:p-5 space-y-5">
          <div className="rounded-lg border border-border bg-surface p-4 flex gap-3">
            <FiInfo className="h-5 w-5 text-primary shrink-0 mt-0.5" />
            <div className="text-sm text-foreground space-y-2 min-w-0">
              <p>
                <span className="font-semibold">Recommendation: {recommended}%</span>
                {dayBal > 6000 ? (
                  <>
                    {' '}
                    — your Webull day balance is above {formatUsd(6000)}. We suggest {recommended}% as a starting daily loss
                    limit; adjust to your comfort level.
                  </>
                ) : (
                  <>
                    {' '}
                    — at {formatUsd(6000)} or below day-start balance, we suggest {recommended}% to help protect smaller
                    accounts from large single-day drawdowns.
                  </>
                )}
              </p>
              <button
                type="button"
                className="text-sm font-semibold text-primary hover:underline"
                onClick={() => setPctInput(String(recommended))}
              >
                Use {recommended}%
              </button>
            </div>
          </div>

          <div>
            <label htmlFor="option-rm-pct" className="block text-sm font-medium text-foreground mb-1.5">
              Daily loss limit (%)
            </label>
            <div className="flex gap-2 items-center">
              <input
                id="option-rm-pct"
                type="number"
                min={1}
                max={100}
                step={0.5}
                value={pctInput}
                onChange={(e) => setPctInput(e.target.value)}
                className="flex-1 min-w-0 px-3 py-2.5 rounded-lg border-2 border-border bg-surface text-foreground focus:border-primary focus:outline-none"
              />
              <span className="text-muted text-sm shrink-0">%</span>
            </div>
            <p className="text-xs text-muted mt-1">Between 1% and 100%. Saved as your account setting (optionRm).</p>
          </div>

          {calculation && (
            <div className="rounded-lg border border-primary/25 bg-primary/5 p-4 space-y-2">
              <p className="text-sm font-semibold text-foreground">{calculation.title}</p>
              <ul className="text-sm text-muted space-y-1.5 list-disc list-inside">
                {calculation.lines.map((line, i) => (
                  <li key={i}>{line}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="rounded-lg border border-border bg-surface p-4">
            <label className="flex items-start gap-3 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={agreedToDisclaimer}
                onChange={handleCheckboxChange}
                className="mt-0.5 h-4 w-4 shrink-0 rounded border-border text-primary focus:ring-2 focus:ring-primary focus:ring-offset-0"
              />

              <span className="text-sm text-foreground leading-snug">
                I Agree, Proceed to Risk Settings
              </span>
            </label>
          </div>
          {showModal && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-3 sm:p-4">
              <div className="bg-white p-4 sm:p-6 rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center gap-3 min-w-0">
                <div>
                <h2 className="text-lg font-semibold mb-3">
                  Risk Disclaimer
                </h2>
                </div>
                <div>
                <button type="button" onClick={onClose} className="text-muted hover:text-foreground p-1 shrink-0" aria-label="Close">
            <FiX className="h-6 w-6" />
          </button>
          </div>
                </div>
                <p className="text-sm text-gray-600">
                  Trading involves risk. Please read carefully before continuing.
                </p>
                <div className="text-sm text-muted space-y-3">
                  <p>
                    TradePilot AI connects you to independent traders on our leaderboard. You are responsible for choosing your risk settings. There is no “safe” option, and each choice carries trade-offs.
                  </p>
                  <p>
                    <span className="font-medium text-foreground">If you enable risk management (5%, 10%, 15%, or 20%):</span>{' '}
                    Your account will be scanned before each trade executes to verify you haven’t reached your daily limit. This scan introduces a processing delay. As a result, your executed price, trade timing, and final results may differ, sometimes significantly, from the master trader’s results. If your limit is hit, you will be excluded from that trade, including any recovery the trader makes afterward.
                  </p>
                  <p>
                    <span className="font-medium text-foreground">If you disable risk management:</span>{' '}
                    Trades will mirror the master trader’s activity as closely as technically possible. You accept full exposure to the trader’s strategy, including drawdowns of any size. If the trader recovers a loss, you recover with them. If they do not, neither do you.
                  </p>
                  <p>
                    TradePilot’s recommendation is a 20% risk management threshold. However, if you have reviewed a trader’s verified performance, win rate, and drawdown history and choose to trust their strategy fully, disabling risk management is a valid choice and entirely yours to make.
                  </p>
                  <p>
                    TradePilot AI is a technology platform. We do not provide investment advice, manage your funds, or guarantee any results. We are not responsible for losses incurred under either setting.
                  </p>
                  <p>
                    By selecting “I Agree,” you confirm that you have read and understand the risks of your chosen setting, that your selection is voluntary and made at your own discretion, and that you release TradePilot AI from liability related to trade outcomes under your chosen configuration.
                  </p>
                </div>
                <div className="mt-5 flex flex-col-reverse sm:flex-row sm:justify-end gap-2 sm:gap-3">
                  <button
                    onClick={() => {
                      setShowModal(false);
                      setAgreedToDisclaimer(false);
                    }}
                    className="w-full sm:w-auto px-4 py-2.5 border-2 border-border rounded-lg font-semibold text-foreground hover:bg-surface transition-colors"
                    >
                    Cancel
                  </button>

                  <button
                    onClick={() => {
                      setAgreedToDisclaimer(true);
                      setShowModal(false);
                    }}
                    className="w-full sm:w-auto bg-[#09a171] text-white px-6 py-2 rounded-lg"
                  >
                    Agree
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="flex flex-col-reverse sm:flex-row gap-2 sm:gap-3 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2.5 border-2 border-border rounded-lg font-semibold text-foreground hover:bg-surface transition-colors"
              disabled={updateMutation.isPending}
            >
              Cancel
            </button>
            <LoadingButton
              type="button"
              onClick={handleSave}
              isLoading={updateMutation.isPending}
              className="flex-1 py-2.5 font-semibold"
              disabled={!validPct || !agreedToDisclaimer}
            >
              Save
            </LoadingButton>
          </div>
        </div>
      </div>
    </div>
  );
}
